/**
 * Evolution Job Number Modal
 * Modal for viewing/editing an order's Evolution job number
 */

import React, { useEffect } from 'react';
import { Form, Input, Modal } from 'antd';
import { IDataverseOrder } from '../../types';
import { getOrderNumber } from '../../utils';

interface EvolutionJobNumberModalProps {
  open: boolean;
  order: IDataverseOrder | null;
  onCancel: () => void;
  onConfirm: (orderId: string, evolutionJobNumber: string) => void;
  loading?: boolean;
}

const EVOLUTION_JOB_NUMBER_RULES = [
  { required: true, message: 'Evolution job number is required' },
  { pattern: /^\d{6}$/, message: 'Evolution job number must be exactly 6 digits' },
];

export const EvolutionJobNumberModal: React.FC<EvolutionJobNumberModalProps> = ({
  open,
  order,
  onCancel,
  onConfirm,
  loading = false,
}) => {
  const [form] = Form.useForm<{ evolutionJobNumber: string }>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ evolutionJobNumber: order?.dogma_evolutionjobnumber ?? '' });
    }
  }, [open, order, form]);

  const handleConfirm = async () => {
    if (!order) return;
    const values = await form.validateFields();
    onConfirm(order.salesorderid, values.evolutionJobNumber);
  };

  return (
    <Modal
      title="Edit Evolution Job Number"
      open={open}
      onOk={handleConfirm}
      onCancel={onCancel}
      confirmLoading={loading}
      okText="Save"
      cancelText="Cancel"
      width={450}
    >
      <div className="space-y-4 py-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Order Number
          </label>
          <div className="text-base font-semibold">{getOrderNumber(order) || 'N/A'}</div>
        </div>

        <Form form={form} layout="vertical">
          <Form.Item
            label="Evolution Job Number"
            name="evolutionJobNumber"
            rules={EVOLUTION_JOB_NUMBER_RULES}
          >
            <Input
              placeholder="Enter 6-digit Evolution job number"
              maxLength={6}
              inputMode="numeric"
              onKeyPress={(e) => {
                if (!/[0-9]/.test(e.key)) e.preventDefault();
              }}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
                form.setFieldValue('evolutionJobNumber', digitsOnly);
              }}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};
